import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useBackNavigation, buildCurrentPath } from '../utils/backNavigation';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Cake,
  DeleteOutline,
  Edit,
  EditCalendar,
  Email,
  EventNote,
  FiberManualRecord,
  FitnessCenter,
  Height,
  History,
  Inventory2,
  LocationOn,
  Person,
  Phone,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { bookingStudioOf, studioIdOf } from '../utils/studioOf';
import { useAuth } from '../contexts/AuthContext';
import { UserNotePanel } from '../components/UserNoteDialog';
import { colors } from '../theme/theme';
import UserFormDialog from '../components/UserFormDialog';
import ManualBookingDialog from '../components/ManualBookingDialog';
import ManualPackAssignDialog from '../components/ManualPackAssignDialog';
import { PackPaymentTypeChip } from '../components/PackBookingTooltip';
import {
  emptyUserRemoveWarnings,
  RemoveUserConfirmMessage,
  UserRemoveWarnings,
} from '../components/RemoveUserConfirmMessage';
import {
  AdminUser,
  getAvatarColor,
  getPlatformDisplay,
  getUserInitials,
  isUserMarketable,
} from '../utils/userHelpers';
import { isExpiredBeforeToday } from '../utils/packExpiry';
import { isStudioScopedRole } from '../utils/roles';
import BranchSelect from '../components/BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';
import { bookingStatusConfig } from '../utils/bookingStatus';

type DetailTab = 'profile' | 'bookings' | 'packs' | 'transactions';

interface UserStats {
  followers: number;
  following: number;
  bookings: number;
  packs: number;
}


const BOOKING_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  workout: { label: 'Workout', color: '#1565C0' },
  event: { label: 'Event', color: '#6A1B9A' },
  wellness: { label: 'Wellness', color: '#00838F' },
  trial: { label: 'Trial', color: '#E65100' },
};

const PAYMENT_MODE_CONFIG: Record<string, { label: string; color: string }> = {
  online: { label: 'Online', color: colors.success },
  offline: { label: 'Offline', color: colors.warning },
  pay_at_venue: { label: 'Pay at Venue', color: colors.info },
  free: { label: 'Free', color: '#78909C' },
  subscription: { label: 'Pack', color: '#6A1B9A' },
};

function formatIST(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function StatusChip({ status }: { status: string }) {
  const cfg = bookingStatusConfig(status);
  return (
    <Chip
      label={cfg.label}
      size="small"
      icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: `${cfg.color} !important` }} />}
      sx={{
        backgroundColor: `${cfg.color}15`,
        color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
}

function TypeChip({ type, config }: { type: string; config: Record<string, { label: string; color: string }> }) {
  const cfg = config[type] || { label: type, color: '#78909C' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: `${cfg.color}15`,
        color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        fontWeight: 500,
        fontSize: '0.7rem',
      }}
    />
  );
}

function BookingHistoryTab({
  userId,
  onNavigateBooking,
  onNavigateStudio,
}: {
  userId: string;
  onNavigateBooking: (bookingId: string) => void;
  onNavigateStudio: (studioId: string) => void;
}) {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const { admin } = useAuth();
  // Server rejects managers/instructors on markBookingPaid — owner and admins only.
  const canMarkPaid = ['admin', 'super_admin', 'studio'].includes(admin?.role || '');

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page: page + 1, pageSize: rowsPerPage, sort: '-1' };
      if (statusFilter) params.status = statusFilter;
      const res = await apiService.getUserBookingHistory(userId, params);
      setBookings(res.bookings || []);
      setTotal(res.count || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [userId, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
  const handleMarkPaid = useCallback(async (booking: any) => {
    const id = booking.bookingId || booking._id;
    setMarkingPaidId(booking._id);
    try {
      await apiService.markBookingPaid(id, bookingStudioOf(booking));
      await fetchBookings();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to mark as paid');
    } finally {
      setMarkingPaidId(null);
    }
  }, [fetchBookings]);

  const [bookingToUpdateStatus, setBookingToUpdateStatus] = useState<any | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusUpdateReason, setStatusUpdateReason] = useState('');
  const [creditClassBack, setCreditClassBack] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const isSubscriptionBooking = (b: any) => b?.transaction?.modeOfPayment === 'subscription';

  const handleOpenStatusUpdate = (booking: any) => {
    setBookingToUpdateStatus(booking);
    setNewStatus(booking.bookingStatus);
    setStatusUpdateReason('');
    setCreditClassBack(true);
  };

  const handleStatusUpdateConfirm = async () => {
    if (!bookingToUpdateStatus || !newStatus) return;
    setIsUpdatingStatus(true);
    try {
      const goingToCancel = newStatus === 'CANCELLED' && bookingToUpdateStatus.bookingStatus !== 'CANCELLED';
      const goingFromCancel =
        bookingToUpdateStatus.bookingStatus === 'CANCELLED' && (newStatus === 'BOOKED' || newStatus === 'CHECKED_IN');
      const sendCreditClassBack =
        (goingToCancel || goingFromCancel) && isSubscriptionBooking(bookingToUpdateStatus) ? creditClassBack : undefined;
      await apiService.updateBookingStatus(
        bookingToUpdateStatus._id,
        newStatus,
        statusUpdateReason || undefined,
        sendCreditClassBack,
        bookingStudioOf(bookingToUpdateStatus),
      );
      setBookings(prev =>
        prev.map(b => (b._id === bookingToUpdateStatus._id ? { ...b, bookingStatus: newStatus } : b)),
      );
      setBookingToUpdateStatus(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to update booking status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {total} booking{total !== 1 ? 's' : ''} total
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="BOOKED">Booked</MenuItem>
            <MenuItem value="CHECKED_IN">Checked In</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
            <MenuItem value="NO_SHOW">No Show</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={fetchBookings}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : bookings.length === 0 ? (
        <Alert severity="info">No bookings found.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.backgroundTertiary }}>
                <TableCell sx={{ fontWeight: 700 }}>Class / Event</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Studio</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Class Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Booked On</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map(b => {
                const className = b.session?.className || b.event?.eventName || '—';
                const activityDate = b.session?.date || b.event?.date || b.wellnessSlotStart;
                const studioId = b.studio?._id;
                const studioName = b.studio?.studioName || '—';
                const paymentMode = b.transaction?.modeOfPayment || b.modeOfPayment || null;
                const isPackBooking = paymentMode === 'subscription';
                const purchasedPack = b.transaction?.purchasedPack || null;
                const amount = (b.transaction?.basePrice ?? 0) + (b.transaction?.taxes ?? 0);

                return (
                  <TableRow
                    key={b._id}
                    hover
                    sx={{ cursor: 'pointer', opacity: b.bookingStatus === 'CANCELLED' ? 0.65 : 1 }}
                    onClick={() => onNavigateBooking(b._id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 220 }}>
                        {className}
                      </Typography>
                    </TableCell>
                    <TableCell
                      onClick={e => {
                        if (studioId) {
                          e.stopPropagation();
                          onNavigateStudio(studioId);
                        }
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          maxWidth: 160,
                          color: studioId ? colors.primary : 'inherit',
                          textDecoration: studioId ? 'underline' : 'none',
                        }}
                      >
                        {studioName}
                      </Typography>
                    </TableCell>
                    <TableCell>{activityDate ? formatDate(activityDate) : '—'}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.25,
                          '& .status-edit-btn': {
                            opacity: 0.45,
                            transition: 'opacity 0.15s, color 0.15s',
                          },
                          '.MuiTableRow-root:hover & .status-edit-btn, & .status-edit-btn:focus-visible': {
                            opacity: 1,
                          },
                        }}
                      >
                        <StatusChip status={b.bookingStatus} />
                        <Tooltip title="Update status">
                          <IconButton
                            className="status-edit-btn"
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenStatusUpdate(b);
                            }}
                            sx={{
                              p: 0.375,
                              color: 'text.secondary',
                              '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                            }}
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {b.bookingType ? (
                        <TypeChip type={b.bookingType} config={BOOKING_TYPE_CONFIG} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {isPackBooking ? (
                        <PackPaymentTypeChip pack={purchasedPack} config={PAYMENT_MODE_CONFIG} />
                      ) : paymentMode ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <TypeChip type={paymentMode} config={PAYMENT_MODE_CONFIG} />
                            {b.isPaid === false && (
                              <Chip
                                label="Pending"
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: colors.warning,
                                  backgroundColor: `${colors.warning}22`,
                                }}
                              />
                            )}
                          </Box>
                          {amount > 0 && (
                            <Typography variant="caption" display="block">
                              ₹{amount.toLocaleString('en-IN')}
                            </Typography>
                          )}
                          {canMarkPaid && b.isPaid === false && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={markingPaidId === b._id}
                              onClick={e => {
                                e.stopPropagation();
                                handleMarkPaid(b);
                              }}
                              sx={{ mt: 0.5, textTransform: 'none', py: 0, minHeight: 24, fontSize: 12 }}
                            >
                              {markingPaidId === b._id ? 'Marking…' : 'Mark as Paid'}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatIST(b.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
          />
        </TableContainer>
      )}

      <Dialog open={Boolean(bookingToUpdateStatus)} onClose={() => setBookingToUpdateStatus(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          {bookingToUpdateStatus && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                Booking: <strong>{bookingToUpdateStatus.bookingId || bookingToUpdateStatus._id}</strong>
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  label="New Status"
                  onChange={e => setNewStatus(e.target.value)}
                >
                  <MenuItem value="BOOKED">Booked</MenuItem>
                  <MenuItem value="CHECKED_IN">Checked In</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="NO_SHOW">No Show</MenuItem>
                </Select>
              </FormControl>
              {newStatus === 'CANCELLED' &&
                bookingToUpdateStatus.bookingStatus !== 'CANCELLED' &&
                isSubscriptionBooking(bookingToUpdateStatus) && (
                <Box sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 1.5, bgcolor: 'warning.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={creditClassBack}
                        onChange={e => setCreditClassBack(e.target.checked)}
                        color="warning"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Credit 1 class back to user's pack</Typography>}
                  />
                </Box>
              )}
              {bookingToUpdateStatus.bookingStatus === 'CANCELLED' &&
                (newStatus === 'BOOKED' || newStatus === 'CHECKED_IN') &&
                isSubscriptionBooking(bookingToUpdateStatus) && (
                <Box sx={{ border: '1px solid', borderColor: 'info.main', borderRadius: 1, p: 1.5, bgcolor: 'info.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={creditClassBack}
                        onChange={e => setCreditClassBack(e.target.checked)}
                        color="info"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Deduct 1 class from user's pack</Typography>}
                  />
                </Box>
              )}
              <TextField
                label="Reason (optional)"
                value={statusUpdateReason}
                onChange={e => setStatusUpdateReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingToUpdateStatus(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdateConfirm}
            disabled={isUpdatingStatus || !newStatus}
          >
            {isUpdatingStatus ? 'Updating…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// A pack is "upcoming" when its startDate is explicitly set to a future date.
const isUpcomingPack = (pack: any) => {
  if (!pack.startDate) return false;
  return new Date(pack.startDate) > new Date();
};

// True when the pack was purchased on today's calendar day.
const isPurchasedToday = (pack: any) => {
  if (!pack.purchaseDate) return false;
  const d = new Date(pack.purchaseDate);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

// True when at least one session has been booked against this pack — once a
// session exists, moving the start date could orphan it, so it's locked.
const hasBookedSession = (pack: any) =>
  (pack.pendingBookingsCount ?? 0) > 0 ||
  (!!pack.totalClasses && (pack.classesLeft ?? 0) < pack.totalClasses);

// The start date may be (re)scheduled for upcoming packs OR packs purchased
// today — not for older active/expired packs, and never once booked.
const canEditPackStartDate = (pack: any) =>
  (isUpcomingPack(pack) || isPurchasedToday(pack)) && !hasBookedSession(pack);

function PackHistoryTab({
  userId,
  onCreateBooking,
}: {
  userId: string;
  onCreateBooking: (pack: any) => void;
}) {
  const [packs, setPacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const [editPack, setEditPack] = useState<any | null>(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isSavingStartDate, setIsSavingStartDate] = useState(false);

  const handleOpenEditStartDate = (pack: any) => {
    setEditPack(pack);
    setEditStartDate(pack.startDate ? new Date(pack.startDate).toISOString().split('T')[0] : '');
    setEditError(null);
  };

  const handleSaveStartDate = async () => {
    if (!editPack || !editStartDate) return;
    setIsSavingStartDate(true);
    setEditError(null);
    try {
      // Pinned to the pack's own location — under "All locations" the
      // switcher sends __all__, which the server refuses for writes.
      const response = await apiService.updatePurchasedPack(editPack._id, { startDate: editStartDate }, studioIdOf(editPack.studio));
      setPacks(prev =>
        prev.map(p => (p._id === editPack._id ? { ...p, ...response.purchasedPack } : p)),
      );
      setEditPack(null);
    } catch (err: any) {
      setEditError(err?.response?.data?.message || err.message || 'Failed to update start date');
    } finally {
      setIsSavingStartDate(false);
    }
  };

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page: page + 1, pageSize: rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      const res = await apiService.getUserPackHistory(userId, params);
      setPacks(res.purchasedPacks || []);
      setTotal(res.count || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load packs');
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, [userId, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          {total} pack purchase{total !== 1 ? 's' : ''} total
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={e => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="frozen">Frozen</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={fetchPacks}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : packs.length === 0 ? (
        <Alert severity="info">No pack purchases found.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.backgroundTertiary }}>
                <TableCell sx={{ fontWeight: 700 }}>Pack</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sessions Left</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Expiry</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packs.map(p => {
                const isExpired =
                  isExpiredBeforeToday(p.expiryDate) ||
                  isExpiredBeforeToday(p.packEndDate);
                const status = p.isFrozen
                  ? 'Frozen'
                  : isUpcomingPack(p)
                    ? 'Upcoming'
                    : isExpired
                      ? 'Expired'
                      : 'Active';
                const statusColor = p.isFrozen
                  ? '#E65100'
                  : isUpcomingPack(p)
                    ? '#1565C0'
                    : isExpired
                      ? '#C62828'
                      : '#2E7D32';
                const expiry = p.expiryDate || p.packEndDate;
                return (
                  <TableRow key={p._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {p.title || p.pack?.title || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {p.classesLeft ?? '—'} / {p.totalClasses ?? '—'}
                    </TableCell>
                    <TableCell>{expiry ? formatDate(expiry) : '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          backgroundColor: `${statusColor}15`,
                          color: statusColor,
                          border: `1px solid ${statusColor}40`,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                        {(status === 'Active' || status === 'Upcoming') && (
                          <Button size="small" variant="outlined" onClick={() => onCreateBooking(p)}
                            sx={{ textTransform: 'none', py: 0, minHeight: 24, fontSize: 12 }}>
                            Book
                          </Button>
                        )}
                        {canEditPackStartDate(p) && (
                          <Tooltip title="Edit start date">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditStartDate(p)}
                              sx={{
                                p: 0.375,
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                              }}
                            >
                              <EditCalendar sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
          />
        </TableContainer>
      )}

      <Dialog open={Boolean(editPack)} onClose={() => setEditPack(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Pack Start Date</DialogTitle>
        <DialogContent>
          {editPack && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                {editPack.title || editPack.pack?.title || 'Pack'}
              </Typography>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="caption" color="text.secondary">
                Rescheduling when this pack becomes active. Expiry date will be recalculated.
              </Typography>
              {editError && <Alert severity="error">{editError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPack(null)} disabled={isSavingStartDate}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStartDate} disabled={isSavingStartDate || !editStartDate}>
            {isSavingStartDate ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function TransactionHistoryTab({ userId }: { userId: string }) {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page: page + 1, pageSize: rowsPerPage, sort: '-1', userId };
      const res = await apiService.getUserTransactionHistory(userId, params);
      setTransactions(res.transactions || []);
      setTotal(res.count || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [userId, page, rowsPerPage]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {total} transaction{total !== 1 ? 's' : ''} total
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={fetchTransactions}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Alert severity="info">No transactions found.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.backgroundTertiary }}>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Booking Reference</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map(t => {
                const amount = (t.basePrice ?? 0) + (t.taxes ?? 0);
                const bookingRef =
                  t.booking?.bookingId ||
                  t.booking?.session?.className ||
                  t.purchasedPack?.title ||
                  '—';
                const status = t.status || t.paymentStatus || 'Completed';
                return (
                  <TableRow key={t._id} hover>
                    <TableCell>{t.createdAt ? formatIST(t.createdAt) : '—'}</TableCell>
                    <TableCell>{amount > 0 ? `₹${amount.toLocaleString('en-IN')}` : '—'}</TableCell>
                    <TableCell>
                      {t.modeOfPayment ? (
                        <TypeChip type={t.modeOfPayment} config={PAYMENT_MODE_CONFIG} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {bookingRef}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={status} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
          />
        </TableContainer>
      )}
    </Box>
  );
}

function ProfileTab({ user }: { user: AdminUser }) {
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);
  const platform = getPlatformDisplay(user);
  return (
    <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '2fr 1fr' }} gap={3}>
      <Box>
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Contact Information
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Email color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {user.email || 'Not provided'}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Phone color="secondary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {user.phoneNumber || 'Not provided'}
                  </Typography>
                </Box>
              </Box>
              {user.city && (
                <Box display="flex" alignItems="center" gap={2}>
                  <LocationOn color="success" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      City
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {user.city}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Personal Details
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
              {user.gender && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Person fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Gender
                    </Typography>
                    <Typography variant="body2">{user.gender}</Typography>
                  </Box>
                </Box>
              )}
              {user.age && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Cake fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Age
                    </Typography>
                    <Typography variant="body2">{user.age} years</Typography>
                  </Box>
                </Box>
              )}
              {user.height && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Height fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Height
                    </Typography>
                    <Typography variant="body2">{user.height} cm</Typography>
                  </Box>
                </Box>
              )}
              {user.weight && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  <FitnessCenter fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Weight
                    </Typography>
                    <Typography variant="body2">{user.weight} kg</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box>
        {/* Platform & Account (SweatSocial platform, marketable, user id) —
            hidden from studio owners; only super-admins see it. */}
        {!isStudioRole && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Platform & Account
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Platform
                </Typography>
                <Box mt={0.5}>
                  <Chip
                    label={platform.label}
                    size="small"
                    sx={{
                      bgcolor: `${platform.color}14`,
                      color: platform.color,
                      fontWeight: 600,
                      border: `1px solid ${platform.color}40`,
                    }}
                  />
                </Box>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Marketable</Typography>
                <Chip
                  label={isUserMarketable(user) ? 'Yes' : 'No'}
                  size="small"
                  color={isUserMarketable(user) ? 'success' : 'default'}
                />
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Member since
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(user.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  User ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {user._id}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        )}

        {(user.associatedStudios?.length ?? 0) > 0 && (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Studios Accessed ({user.associatedStudios!.length})
              </Typography>
              {user.associatedStudios!.map((entry, i) => {
                const studioName =
                  typeof entry.studio === 'object' ? entry.studio.studioName : String(entry.studio);
                return (
                  <Box key={i} py={0.75}>
                    <Typography variant="body2" fontWeight={600}>
                      {studioName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      via {entry.source}
                    </Typography>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}

const TAB_INDEX: Record<DetailTab, number> = {
  profile: 0,
  bookings: 1,
  packs: 2,
  transactions: 3,
};

const INDEX_TAB: DetailTab[] = ['profile', 'bookings', 'packs', 'transactions'];

const UserDetailPage: React.FC = () => {
  const { admin } = useAuth();
  const isStudioRole = isStudioScopedRole(admin?.role);
  // Under "All locations" a removal must name the location it detaches the
  // member from — `__all__` is refused for writes.
  const { isAllLocations } = useMyLocations();
  const mustPickRemoveBranch = isStudioRole && isAllLocations;
  const [removeBranch, setRemoveBranch] = useState<string>('');
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const handleBack = useBackNavigation('/users');

  const activeTab = (searchParams.get('tab') as DetailTab) || 'profile';
  const tabIndex = TAB_INDEX[activeTab] ?? 0;

  const [user, setUser] = useState<AdminUser | null>(null);
  const [userStats, setUserStats] = useState<UserStats>({
    followers: 0,
    following: 0,
    bookings: 0,
    packs: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [assignPackOpen, setAssignPackOpen] = useState(false);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  // When booking is launched from a specific pack (Packs tab "Book" action),
  // the studio must be locked to that pack's own studio — never the ambient
  // super-admin studio selection, which could point at a different studio.
  const [bookingPackPrefill, setBookingPackPrefill] = useState<{ packId: string; studio?: any; startDate?: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [studios, setStudios] = useState<any[]>([]);
  const [removeWarnings, setRemoveWarnings] = useState<UserRemoveWarnings>(
    emptyUserRemoveWarnings(),
  );

  const fetchUserDetails = useCallback(async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getUserById(userId);
      setUser(response.data);
      if (response.stats) {
        setUserStats(response.stats);
      } else {
        setUserStats({
          followers: response.data?.followers?.length || 0,
          following: response.data?.followings?.length || 0,
          bookings: 0,
          packs: 0,
        });
      }
      // Booking & pack counts are role-aware: a studio owner gets only THEIR
      // studio's counts (not all SweatSocial bookings); admins get the total.
      try {
        const [bk, pk] = await Promise.all([
          apiService.getUserBookingHistory(userId, { page: 1, pageSize: 1 }),
          apiService.getUserPackHistory(userId, { page: 1, pageSize: 1 }),
        ]);
        setUserStats(s => ({ ...s, bookings: bk?.count ?? 0, packs: pk?.count ?? 0 }));
      } catch { /* leave counts at 0 */ }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user details');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserDetails();
    apiService.getStudios().then(res => setStudios(res.data || res.studios || [])).catch(() => {});
  }, [fetchUserDetails]);

  useEffect(() => {
    if (!userId) {
      setRemoveWarnings(emptyUserRemoveWarnings());
      return;
    }
    apiService
      .getUserRemoveWarnings(userId)
      .then(setRemoveWarnings)
      .catch(() => setRemoveWarnings(emptyUserRemoveWarnings()));
  }, [userId]);

  const setTab = (tab: DetailTab) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (tab === 'profile') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  const currentDetailPath = useMemo(
    () => buildCurrentPath(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const handleNavigateBooking = (bookingId: string) => {
    navigate(`/bookings/${bookingId}`, { state: { from: currentDetailPath } });
  };

  const handleNavigateStudio = (studioId: string) => {
    navigate(`/studios/${studioId}`, { state: { from: currentDetailPath } });
  };

  // Launching a booking from a specific pack must lock the studio to that
  // pack's own studio — resolved from the pack itself, never the ambient
  // super-admin studio selector — so it can't silently book against the
  // wrong studio (and, per the fixed dialog bug, no longer clobbers the
  // already-known user either).
  const handleCreateBookingFromPack = (pack: any) => {
    const studio = pack.studio?._id
      ? (studios.find((s: any) => s?._id === pack.studio._id) ?? {
          _id: pack.studio._id,
          studioName: pack.studio.studioName || 'Studio',
        })
      : undefined;
    setBookingPackPrefill({ packId: pack._id, studio, startDate: pack.startDate });
    setManualBookingOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!user) return;
    if (mustPickRemoveBranch && !removeBranch) return;
    setIsDeleting(true);
    try {
      await apiService.deleteUser(user._id, mustPickRemoveBranch ? removeBranch : undefined);
      navigate((location.state as { from?: string } | null)?.from || '/users');
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton width={80} height={36} sx={{ mb: 2 }} />
        <Box display="flex" gap={2} mb={3}>
          <Skeleton variant="circular" width={64} height={64} />
          <Box flex={1}>
            <Skeleton width={220} height={36} />
            <Skeleton width={180} height={24} />
          </Box>
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={3}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} variant="rounded" height={90} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={360} />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2, textTransform: 'none' }}>
          Back
        </Button>
        <Alert severity="error" action={<Button onClick={fetchUserDetails}>Retry</Button>}>
          {error || 'User not found'}
        </Alert>
      </Box>
    );
  }

  const platform = getPlatformDisplay(user);

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2, textTransform: 'none' }}>
        Back
      </Button>

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" gap={2} alignItems="center">
          <Avatar
            src={user.image}
            sx={{
              width: 64,
              height: 64,
              bgcolor: getAvatarColor(user.name),
              fontWeight: 700,
              fontSize: '1.25rem',
              border: `3px solid ${colors.divider}`,
            }}
          >
            {getUserInitials(user.name)}
          </Avatar>
          <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h5" fontWeight={700}>
                {user.name || 'Anonymous User'}
              </Typography>
              {/* Online/offline status is hidden from the view by default (both roles). */}
              {/* Platform (SweatSocial/source) badge is hidden from studio owners. */}
              {!isStudioRole && (
                <Chip
                  size="small"
                  label={platform.label}
                  sx={{
                    bgcolor: `${platform.color}14`,
                    color: platform.color,
                    fontWeight: 600,
                    border: `1px solid ${platform.color}40`,
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {user.email || 'No email'}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <Button variant="outlined" size="small" startIcon={<Edit fontSize="small" />} onClick={() => setEditOpen(true)} sx={{ textTransform: 'none' }}>
            Edit
          </Button>
          <Button variant="outlined" size="small" startIcon={<Inventory2 fontSize="small" />} onClick={() => setAssignPackOpen(true)} sx={{ textTransform: 'none' }}>
            Assign a pack
          </Button>
          <Button variant="outlined" size="small" startIcon={<EventNote fontSize="small" />}
            onClick={() => { setBookingPackPrefill(null); setManualBookingOpen(true); }}
            sx={{ textTransform: 'none' }}>
            Create booking
          </Button>
          <Button variant="outlined" size="small" color="error" startIcon={<DeleteOutline fontSize="small" />}
            onClick={() => {
              setRemoveBranch('');
              setDeleteDialogOpen(true);
            }}
            sx={{ textTransform: 'none' }}>
            {isStudioRole ? 'Remove' : 'Delete'}
          </Button>
        </Box>
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: 'repeat(2, 1fr)', md: isStudioRole ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)' }} gap={2} mb={3}>
        {[
          // Followers/Following are SweatSocial social-graph data — hidden from studio owners.
          ...(isStudioRole ? [] : [
            { label: 'Followers', value: userStats.followers, tab: null },
            { label: 'Following', value: userStats.following, tab: null },
          ]),
          { label: 'Bookings', value: userStats.bookings, tab: 'bookings' as DetailTab },
          { label: 'Packs', value: userStats.packs, tab: 'packs' as DetailTab },
        ].map(stat => (
          <Paper
            key={stat.label}
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: stat.tab ? 'pointer' : 'default',
              borderRadius: 2,
              '&:hover': stat.tab ? { borderColor: colors.primary } : undefined,
            }}
            onClick={() => stat.tab && setTab(stat.tab)}
          >
            <Typography variant="h5" fontWeight={700}>
              {stat.value.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stat.label}
            </Typography>
          </Paper>
        ))}
      </Box>

      {userId && <UserNotePanel userId={userId} userName={user.name ?? undefined} />}

      <Tabs
        value={tabIndex}
        onChange={(_, idx) => setTab(INDEX_TAB[idx])}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Profile" icon={<Person sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ textTransform: 'none' }} />
        <Tab label="Bookings" icon={<EventNote sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ textTransform: 'none' }} />
        <Tab label="Packs" icon={<Inventory2 sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ textTransform: 'none' }} />
        <Tab label="Transactions" icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ textTransform: 'none' }} />
      </Tabs>

      {activeTab === 'profile' && <ProfileTab user={user} />}
      {activeTab === 'bookings' && userId && (
        <BookingHistoryTab
          userId={userId}
          onNavigateBooking={handleNavigateBooking}
          onNavigateStudio={handleNavigateStudio}
        />
      )}
      {activeTab === 'packs' && userId && (
        <PackHistoryTab userId={userId} onCreateBooking={handleCreateBookingFromPack} />
      )}
      {activeTab === 'transactions' && userId && <TransactionHistoryTab userId={userId} />}

      <UserFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSuccess={fetchUserDetails}
        user={user}
      />

      <ManualPackAssignDialog
        open={assignPackOpen}
        onClose={() => setAssignPackOpen(false)}
        onSuccess={() => setAssignPackOpen(false)}
        studios={studios}
        prefilledUser={{
          _id: user._id,
          name: user.name || undefined,
          email: user.email || undefined,
          phoneNumber: user.phoneNumber || undefined,
          gender: user.gender,
        }}
      />

      <ManualBookingDialog
        open={manualBookingOpen}
        onClose={() => { setManualBookingOpen(false); setBookingPackPrefill(null); }}
        onSuccess={() => { setManualBookingOpen(false); setBookingPackPrefill(null); }}
        studios={studios}
        prefilledUser={{
          _id: user._id,
          name: user.name || undefined,
          email: user.email || undefined,
          phoneNumber: user.phoneNumber || undefined,
        }}
        prefilledPackId={bookingPackPrefill?.packId}
        prefilledPackStartDate={bookingPackPrefill?.startDate}
        prefilledStudio={bookingPackPrefill?.studio}
        allowGenericEventBooking
      />

      {deleteDialogOpen && (
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>{isStudioRole ? 'Remove from studio' : 'Delete user'}</DialogTitle>
          <DialogContent>
            <RemoveUserConfirmMessage
              name={user.name || 'This user'}
              warnings={removeWarnings}
              isStudioRole={isStudioRole}
            />
            {mustPickRemoveBranch && (
              <Box sx={{ mt: 2 }}>
                <BranchSelect
                  value={removeBranch}
                  onChange={(v) => setRemoveBranch(v || '')}
                  label="Remove from location"
                  minRole="manager"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteUser}
              disabled={isDeleting || (mustPickRemoveBranch && !removeBranch)}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UserDetailPage;
